// FileUploadButtons.tsx
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';

import './styles.css';

import { AddCircleOutline, ArrowBack, Delete, InsertDriveFile } from '@mui/icons-material';

// @ts-ignorets in
import passport from '../../static/images/icons/passport.png';
// @ts-ignorets in
import driverLincence from '../../static/images/icons/driver-license.png';
// @ts-ignorets in
import idImage from '../../static/images/icons/licence.png';
import { updateEnroll } from 'Helpers/update-enroll';
import { useEnrollForm } from 'Contexts/EnrollProvider/EnrollProvider';
import { uploadImage } from 'Helpers/upload-img';
import { ipayNet, newServer } from 'constants/constants';

interface FileUploadButtonsProps {
  data?: any;
  onClose: (value: boolean) => void;
  open: boolean;
}

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const ACCEPTED_EXT_TEXT = 'PDF, JPG, JPEG or PNG';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Los colores salen del tema, no de constantes locales.
 *
 * Antes habia aqui un verde lima (`#9ACD32`), su hover y un azul (`#03a9f4`)
 * escritos a mano, y el texto del boton fijo en negro. Eso no puede funcionar
 * en dos modos: el verde claro del modo oscuro pide texto oscuro encima, y el
 * verde oscurecido del modo claro pide texto blanco. Ningun color de texto fijo
 * sirve para los dos.
 *
 * Por eso el boton usa `color='success'` y deja que MUI elija el contraste con
 * `contrastText` (que `muiTheme.ts` ya resuelve por modo).
 */

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const validateFile = (file: File): string | null => {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return `Invalid file type "${file.type || 'unknown'}". Allowed: ${ACCEPTED_EXT_TEXT}.`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File "${file.name}" is too large (${formatBytes(file.size)}). Max allowed: ${MAX_FILE_SIZE_MB} MB.`;
  }
  return null;
};

const isPdfUrl = (url: string) => /\.pdf(\?|$)/i.test(url || '');

// Walks the enroll data tree and returns the entry holding info for a given SSN.
// Returns { node, container } where `container` is the parent reference (root, controller or owners[i]).
const findHolderBySSN = (root: any, ssn: string): any | null => {
  if (!root) return null;
  if ('ssn' in root && String(root.ssn || '') === String(ssn || '')) return root;
  // The controller is the primary applicant, whose document must live at the
  // enrollment root for the final processor submission.
  if (String(root.controller?.ssn || '') === String(ssn || '')) return root;
  if (Array.isArray(root.owners)) {
    for (const owner of root.owners) {
      const hit = findHolderBySSN(owner, ssn);
      if (hit) return hit;
    }
  }
  return null;
};

const FileUploadButtons: React.FC<FileUploadButtonsProps> = (props: FileUploadButtonsProps) => {
  const { data, onClose, open } = props;
  const [idType, setIdType] = useState<string | undefined>();
  const [savedIdType, setSavedIdType] = useState<string | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFile2, setSelectedFile2] = useState<File | null>(null);
  // URLs already stored in the server for this user.
  const [existingUrls, setExistingUrls] = useState<string[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  // Mark which existing URLs the user wants to remove (sent on save).
  const [removedExisting, setRemovedExisting] = useState<{ front: boolean; back: boolean }>({
    front: false,
    back: false,
  });
  const { saveEnrrollData } = useEnrollForm();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // On open, hydrate state from the enroll data so the user sees what's already saved.
  useEffect(() => {
    if (!open) return;
    const canonicalHolder = findHolderBySSN(data?.enrollData?.data, data?.ssn);
    const legacyController =
      String(canonicalHolder?.controller?.ssn || '') === String(data?.ssn || '')
        ? canonicalHolder.controller
        : null;
    const storedHolder = canonicalHolder?.userIdImgs?.length
      ? canonicalHolder
      : legacyController?.userIdImgs?.length
      ? legacyController
      : canonicalHolder;

    if (storedHolder?.userIdImgs?.length) {
      setExistingUrls(storedHolder.userIdImgs);
      setIdType(storedHolder.typeImage);
      setSavedIdType(storedHolder.typeImage);
    } else {
      setExistingUrls([]);
      setIdType(undefined);
      setSavedIdType(undefined);
    }
    setPreviewUrls({});
    setSelectedFile(null);
    setSelectedFile2(null);
    setRemovedExisting({ front: false, back: false });
    setErrorMsg(null);

    const storedUrls: string[] = storedHolder?.userIdImgs || [];
    Promise.all(
      storedUrls.map(async (storedUrl) => {
        try {
          const key = new URL(storedUrl).pathname.replace(/^\//, '');
          const response = await fetch(
            `${newServer}aws/get-identification-request?key=${encodeURIComponent(key)}`,
            {
              headers: {
                'x-api-key': ipayNet.apiToken,
                Authorization: `Bearer ${data?.user?.token}`,
              },
            }
          );
          if (!response.ok) return null;
          const result = await response.json();
          return result?.url ? ([storedUrl, result.url] as const) : null;
        } catch (_error) {
          return null;
        }
      })
    ).then((entries) => {
      const resolved = entries.filter(Boolean) as ReadonlyArray<readonly [string, string]>;
      setPreviewUrls(Object.fromEntries(resolved));
    });
  }, [open, data]);

  const requiresBack = idType === 'license' || idType === 'idCard';

  // What will be the front/back after applying user actions (existing - removed + newly selected).
  const canReuseExisting = idType === savedIdType;
  const finalFront = canReuseExisting && !removedExisting.front && existingUrls[0]
    ? { kind: 'existing' as const, url: existingUrls[0] }
    : selectedFile
    ? { kind: 'new' as const, file: selectedFile }
    : null;

  const finalBack = canReuseExisting && !removedExisting.back && existingUrls[1]
    ? { kind: 'existing' as const, url: existingUrls[1] }
    : selectedFile2
    ? { kind: 'new' as const, file: selectedFile2 }
    : null;

  const canUpload = Boolean(idType && finalFront && (!requiresBack || finalBack));
  const validationHint = !idType
    ? 'Choose a document type to start.'
    : !finalFront
    ? 'Please attach the FRONT of the document.'
    : requiresBack && !finalBack
    ? 'Please attach the BACK of the document as well.'
    : null;

  const handleButtonClick = (idType: string) => {
    setErrorMsg(null);
    setIdType(idType);
  };

  const handleChangeDocumentType = () => {
    setIdType(undefined);
    setSelectedFile(null);
    setSelectedFile2(null);
    setRemovedExisting({ front: false, back: false });
    setErrorMsg(null);
  };

  const handleCloseDialog = (force: boolean) => {
    if (force) {
      onClose(true);
      return;
    }
    if (idType && !existingUrls.length) {
      setSelectedFile(null);
      setSelectedFile2(null);
      setErrorMsg(null);
      setIdType(undefined);
    } else {
      onClose(true);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>, isBack: boolean) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setErrorMsg(error);
      event.target.value = '';
      return;
    }

    setErrorMsg(null);
    if (isBack) {
      setSelectedFile2(file);
      setRemovedExisting((s) => ({ ...s, back: true }));
    } else {
      setSelectedFile(file);
      setRemovedExisting((s) => ({ ...s, front: true }));
    }
  };

  const handleRemove = (isBack: boolean) => {
    if (isBack) {
      setSelectedFile2(null);
      setRemovedExisting((s) => ({ ...s, back: true }));
    } else {
      setSelectedFile(null);
      setRemovedExisting((s) => ({ ...s, front: true }));
    }
  };

  const handleUpload = async () => {
    if (!canUpload) {
      setErrorMsg(validationHint || 'Please complete all required documents before uploading.');
      return;
    }
    setErrorMsg(null);
    setLoading(true);
    try {
      const finalImgsUrl: string[] = [];

      if (finalFront?.kind === 'new') {
        finalImgsUrl.push(await uploadImage(finalFront.file));
      } else if (finalFront?.kind === 'existing') {
        finalImgsUrl.push(finalFront.url);
      }

      if (requiresBack && finalBack) {
        if (finalBack.kind === 'new') {
          finalImgsUrl.push(await uploadImage(finalBack.file));
        } else {
          finalImgsUrl.push(finalBack.url);
        }
      }

      // Work on a copy so a failed request cannot make the UI look saved locally.
      const nextEnrollData = JSON.parse(JSON.stringify(data.enrollData.data));
      const holder = findHolderBySSN(nextEnrollData, data.ssn);
      if (holder) {
        holder.userIdImgs = finalImgsUrl;
        holder.typeImage = idType;
        if (String(holder.controller?.ssn || '') === String(data.ssn || '')) {
          delete holder.controller.userIdImgs;
          delete holder.controller.typeImage;
        }
      } else {
        throw new Error('The applicant record could not be found. Please reload and try again');
      }

      await updateEnroll(data.user, {
        user_id: data.user?.user_id,
        info_dwolla: nextEnrollData,
      });

      saveEnrrollData(nextEnrollData);
      setLoading(false);
      setSuccessMsg('Document(s) uploaded and saved successfully.');
      onClose(true);
    } catch (error: any) {
      setLoading(false);
      const message = error?.message || 'Unknown error while uploading the document.';
      setErrorMsg(`Upload failed: ${message}. Please verify the file and try again.`);
      console.error('UploadId.handleUpload', error);
    }
  };

  const renderPreview = (slot: 'front' | 'back') => {
    const value = slot === 'front' ? finalFront : finalBack;
    if (!value) {
      return (
        <span className='icon-container'>
          <AddCircleOutline fontSize='large' />
        </span>
      );
    }
    if (value.kind === 'new') {
      const isImage = value.file.type.startsWith('image/');
      return isImage ? (
        <img className='image' src={URL.createObjectURL(value.file)} alt={value.file.name} />
      ) : (
        <span className='icon-container' title={value.file.name}>
          <InsertDriveFile fontSize='large' style={{ color: 'var(--brand)' }} />
        </span>
      );
    }
    // Existing URL
    const previewUrl = previewUrls[value.url];
    if (!previewUrl) {
      return (
        <span className='icon-container' title='Saved document'>
          <InsertDriveFile fontSize='large' style={{ color: 'var(--brand)' }} />
        </span>
      );
    }
    if (isPdfUrl(value.url)) {
      return (
        <a href={previewUrl} target='_blank' rel='noreferrer' title='Open saved PDF'>
          <InsertDriveFile fontSize='large' style={{ color: 'var(--brand)' }} />
        </a>
      );
    }
    return (
      <a href={previewUrl} target='_blank' rel='noreferrer' title='Open saved file'>
        <img className='image' src={previewUrl} alt='Saved document' />
      </a>
    );
  };

  const renderInfoLine = (slot: 'front' | 'back') => {
    const value = slot === 'front' ? finalFront : finalBack;
    if (!value) {
      return (
        <Typography variant='caption' style={{ color: 'var(--text-subtle)', display: 'block', marginTop: 6 }}>
          Allowed: {ACCEPTED_EXT_TEXT} · Max {MAX_FILE_SIZE_MB} MB
        </Typography>
      );
    }
    const label =
      value.kind === 'new'
        ? `${value.file.name} (${formatBytes(value.file.size)})`
        : 'Previously uploaded file';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <Typography
          variant='caption'
          style={{ color: 'var(--success)', wordBreak: 'break-all', flex: 1 }}
        >
          ✓ {label}
        </Typography>
        <IconButton
          size='small'
          onClick={() => handleRemove(slot === 'back')}
          title='Remove this file'
          style={{ color: 'var(--danger)' }}
        >
          <Delete fontSize='small' />
        </IconButton>
      </div>
    );
  };

  const renderInputBlock = (slot: 'front' | 'back', label: string, inputId: string) => (
    <DialogContent className='document-add-container'>
      <DialogContentText component='div'>
        <span className='icon-text'>
          {renderPreview(slot)}
          <div>{label}</div>
        </span>
      </DialogContentText>
      <div className='file-input-container'>
        <label htmlFor={inputId} className='file-input-label'>
          <input
            type='file'
            accept='.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
            id={inputId}
            className='file-input'
            onChange={(e) => handleFileInputChange(e, slot === 'back')}
          />
          {(slot === 'front' ? finalFront : finalBack) ? 'Change File' : 'Choose File'}
        </label>
      </div>
      {renderInfoLine(slot)}
    </DialogContent>
  );

  return (
    <Grid>
      <Dialog open={open} fullWidth>
        <DialogContent className='dialog-container' style={{ backgroundColor: 'var(--surface)' }}>
          {errorMsg && (
            <Alert severity='error' onClose={() => setErrorMsg(null)} style={{ marginBottom: 12 }}>
              {errorMsg}
            </Alert>
          )}
          {!errorMsg && validationHint && idType && (
            <Alert severity='warning' style={{ marginBottom: 12 }}>
              {validationHint}
            </Alert>
          )}

          {idType && (
            <Button
              startIcon={<ArrowBack />}
              onClick={handleChangeDocumentType}
              disabled={loading}
              style={{ color: 'var(--text)', marginBottom: 8 }}
            >
              Change document type
            </Button>
          )}

          {!idType && (
            <Grid className='flex document-add-container-modal'>
              <Button
                variant='contained'
                className='button'
                onClick={() => handleButtonClick('passport')}
              >
                <img className='icon-card' src={passport} alt='Passport' />
                <AddCircleOutline className='icon' />
                Passport
              </Button>

              <Button
                variant='contained'
                className='button'
                onClick={() => handleButtonClick('idCard')}
              >
                <img className='icon-card' src={idImage} alt='ID' />
                <AddCircleOutline className='icon' />
                ID
              </Button>

              <Button
                variant='contained'
                className='button'
                onClick={() => handleButtonClick('license')}
              >
                <img className='icon-card' src={driverLincence} alt="Driver's License" />
                <AddCircleOutline className='icon' />
                Driver's License
              </Button>
            </Grid>
          )}

          {(idType === 'license' || idType === 'idCard') && (
            <div>
              <DialogTitle style={{ color: 'var(--text)' }}>
                {idType === 'license' ? 'Upload Driver license' : 'National Id'}
              </DialogTitle>
              <Grid className='flex'>
                {renderInputBlock('front', 'Upload the front of your document', 'file-input-front')}
                {renderInputBlock('back', 'Upload the back of your document', 'file-input-back')}
              </Grid>
            </div>
          )}

          {idType === 'passport' && (
            <div>
              <DialogTitle style={{ color: 'var(--text)' }}>Upload Passport</DialogTitle>
              {renderInputBlock('front', 'Upload your document', 'file-input-front')}
            </div>
          )}

          <DialogActions style={{ justifyContent: 'space-between' }}>
            <Button
              variant='contained'
              color='error'
              style={{
                marginRight: '8px',
                width: '500px',
                height: '50px',
                borderRadius: '15px',
                fontSize: '1rem',
              }}
              onClick={() => handleCloseDialog(true)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='success'
              style={{
                width: '500px',
                height: '50px',
                borderRadius: '15px',
                fontSize: '1rem',
                fontWeight: 700,
              }}
              onClick={handleUpload}
              disabled={!canUpload || loading}
            >
              {loading && (
                <CircularProgress size={20} color='inherit' style={{ marginRight: 8 }} />
              )}
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity='success' onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default FileUploadButtons;
